/**
 * Safely converts a value to a number, returning null if the conversion results in NaN
 * This prevents invalid number conversions from propagating through the application
 *
 * @param value - The value to convert to a number (string, number, or other)
 * @returns The converted number, or null if the conversion is invalid
 */
export function safeToNumber(value: string | number | undefined | null): number | null {
  // Handle null/undefined cases
  if (value === null || value === undefined) {
    return null;
  }

  // If it's already a number, check if it's valid
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  // Handle string conversion
  if (typeof value === 'string') {
    // Handle empty strings
    if (value.trim() === '') {
      return null;
    }

    const converted = Number(value);
    return Number.isNaN(converted) ? null : converted;
  }

  // For any other type, attempt conversion
  const converted = Number(value);
  return Number.isNaN(converted) ? null : converted;
}

/**
 * Filters out null values from an array of converted numbers
 * Useful when processing arrays where some IDs might be invalid
 *
 * @param values - Array of potentially null numbers
 * @returns Array containing only valid numbers
 */
export function filterValidNumbers(values: (number | null)[]): number[] {
  return values.filter((value): value is number => value !== null);
}
